/**
 * Post–birthday welcome for the remote adaptive screener: sets context and the
 * "how this works" expectations before the one-question-at-a-time walk, mirroring
 * the fixed-survey welcome (`../WelcomeScreen`). The screener is adaptive — no
 * fixed question count and no patient name is disclosed here — so the copy is
 * count-free and identity-free while keeping the same visual language.
 * @agentic members
 */

import { Paper, Stack, Title, Text, Button, Divider } from "@mantine/core";

import { SurveyWordmark } from "../SurveyWordmark";
import { getAdvocateDisplayName } from "../surveyEnv";

type ScreenerWelcomeScreenProps = {
  /** First-time vs returning framing, resolved server-side (D18). */
  kind?: "first-check-in" | "follow-up";
  /** Advocate display name resolved post-verification (D16), if any. */
  advocateDisplayName?: string;
  onStart: () => void;
};

/**
 * Welcome and expectations screen shown before the adaptive screener walk.
 * @agentic members
 */
export const ScreenerWelcomeScreen = ({
  kind = "first-check-in",
  advocateDisplayName,
  onStart,
}: ScreenerWelcomeScreenProps) => {
  const advocate = advocateDisplayName ?? getAdvocateDisplayName();
  const welcomeTitle =
    kind === "follow-up" ? "Welcome back" : "Your mental health check-in";

  return (
    <Paper radius="lg" p="xl" shadow="sm" className="welcome-card">
      <Stack gap="lg">
        <SurveyWordmark />
        <Stack gap="xs">
          <Title order={2} c="#391640">
            {welcomeTitle}
          </Title>
          {advocate && (
            <Text size="sm" style={{ opacity: 0.5, color: "#2a1a2e" }}>
              from {advocate}
            </Text>
          )}
        </Stack>

        <Divider />

        <Paper p="md" radius="md" withBorder shadow="none">
          <Stack gap="md">
            <Text
              size="xs"
              tt="uppercase"
              fw={600}
              style={{
                letterSpacing: "0.04em",
                opacity: 0.35,
                color: "#2a1a2e",
              }}
            >
              How this works
            </Text>
            <Stack gap="sm">
              <FaqBlock
                title="What is this?"
                body="A quick check-in on how you've been feeling — like a thermometer for your mental health."
              />
              <FaqBlock
                title="How long?"
                body="Just a few minutes. Questions are asked one at a time and adapt to your answers."
              />
              <FaqBlock
                title="Do I have to answer everything?"
                body="No — you can skip any question, or a whole section, and still finish."
              />
              <FaqBlock
                title="Who sees this?"
                body="Your Benchmark care team sees your answers to help coordinate your care."
              />
            </Stack>
          </Stack>
        </Paper>

        <Button size="lg" fullWidth onClick={onStart}>
          Start check-in
        </Button>

        <Text size="xs" ta="center" style={{ opacity: 0.35, color: "#2a1a2e" }}>
          🔒 Encrypted · shared with your care team
        </Text>
      </Stack>
    </Paper>
  );
};

const FaqBlock = ({ title, body }: { title: string; body: string }) => (
  <Stack gap={4}>
    <Text size="sm" fw={600} c="#2a1a2e">
      {title}
    </Text>
    <Text size="sm" style={{ opacity: 0.55, color: "#2a1a2e" }}>
      {body}
    </Text>
  </Stack>
);
